import { useEffect, useState } from "react";

import { Column, TableViews } from "@/components";
import { PlacementTrackerStats } from "@/components/placement-tracker/PlacementTrackerStats";
import { PlacementTrackerTable } from "@/components/placement-tracker/PlacementTrackerTable";
import { PlacementTrackerTableQueryOptions } from "@/components/placement-tracker/PlacementTrackerTableQueryOptions";

import { useDefaultViewGuard } from "@/hooks/useDefaultViewGuard";
import { useSetTitle } from "@/hooks/useSetTitle";
import { useUpdateView } from "@/hooks/mutations/useUpdateView";
import { useGetPlacementTracker } from "@/hooks/queries/useGetPlacementTracker";
import { useGetViews } from "@/hooks/queries/useGetViews";
import { useSetView } from "@/hooks/sets/useSetView";
import { defaultPlacementQueryParams } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import { PlacementQueryParams, View, ViewTableName } from "@/types";

const viewQueryParams = {
  tableName: ViewTableName.PLACEMENT_TRACKER,
};

export const PlacementTrackerPage = () => {
  /* useStates */
  const [selectedView, setSelectedView] = useState<View | null>(null);

  /* Database Queries and Cache Updates */
  // Reads
  const { data: placements, status: placementsStatus } = useGetPlacementTracker(
    (selectedView?.queryParams as PlacementQueryParams | null) ??
      defaultPlacementQueryParams,
  );
  const { data: views, status: viewsStatus } = useGetViews({
    tableName: ViewTableName.PLACEMENT_TRACKER,
  });

  // Updates
  const { mutate: updateView } = useUpdateView();
  const setView = useSetView();

  useSetTitle({
    title: "Placement Tracker",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.PLACEMENT_TRACKER);
  }, []);

  // Ensure a user has a view for this page
  useDefaultViewGuard({
    views,
    selectedView,
    setSelectedView,
    tableName: ViewTableName.PLACEMENT_TRACKER,
    defaultQueryParams: defaultPlacementQueryParams,
  });

  if (placementsStatus === "pending" || viewsStatus === "pending") {
    // TODO: Loading state.
    return null;
  } else if (placementsStatus === "error" || viewsStatus === "error") {
    // TODO: Error state.
    return null;
  }

  const handleUpdateView = (queryParams: PlacementQueryParams) => {
    if (!selectedView) return;

    mutations.updateView({
      setSelectedView: setSelectedView,
      setView: setView,
      updateView: updateView,
      updateViewRequest: { queryParams },
      viewId: selectedView.id,
      viewQueryParams: viewQueryParams,
    });
  };

  // Prevents a flash of a page with default query options.
  // If the default view has query options, it will refresh to those slightly after which looks glitchy
  if (!selectedView) {
    return null;
  }

  const placementTrackerQueryParams =
    selectedView.queryParams as PlacementQueryParams;
  return (
    <Column className="gap-4 p-6">
      <PlacementTrackerStats placements={placements} />
      <TableViews
        defaultQueryParams={defaultPlacementQueryParams}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        tableName={ViewTableName.PLACEMENT_TRACKER}
        viewQueryParams={viewQueryParams}
        views={views}
      />
      <PlacementTrackerTableQueryOptions
        placementTrackerQueryParams={placementTrackerQueryParams}
        handleUpdateView={handleUpdateView}
      />
      <div className="max-h-[calc(100vh-13rem)] overflow-auto">
        <PlacementTrackerTable
          groupBy={placementTrackerQueryParams.groupBy}
          handleUpdateView={handleUpdateView}
          placementTrackerQueryParams={placementTrackerQueryParams}
          placements={placements}
        />
      </div>
    </Column>
  );
};
